import { ChatSettingBase } from "@core/base/chat-setting-base";
import { ChatSkillSelectionStore } from '@core/base/chat-skill-selection-store';
import { IQuickActionUser } from "@common/microservice-types/db/quick-action/quick-action.user";
import { IUserSkill } from "@common/microservice-types/db/skill/skill.user";
import { OfficeChatRepository } from "@core/office/office-chat-repository/office-chat-repository";
import { OfficeService } from "@core/office/office-service/office-service";
import { OfficeSessionService } from "@core/office/office-session/office.session.service";
import type { IChatSetting } from '@core/types/chat-setting.types';
import { AiModelsService } from "@services/ai-models/ai-models.service";
import { QuickActionsService } from "@services/quick-actions/quick-actions.service";
import { UserSettingsApiService } from "@services/user-settings-api/user-settings-api.service";
import { InstructionsService } from "@services/instructions/instructions.service";
import { combineLatest, map, Observable, Subscription } from "rxjs";
import Office from '@core/shared/office.instance';
import
{
    OfficeChatSettingAdapterV1
} from "./office-chat-setting-v1/office-chat-setting-adapter-v1";

export class OfficeChatSetting extends ChatSettingBase implements ChatSettingBase
{
    private officeChatSetting: OfficeChatSettingAdapterV1 = new OfficeChatSettingAdapterV1(
        this.office,
        this.session,
        this.chatRepository,
        this.aiModels,
        this.quickActionsService,
        this.userSettingsApi,
        this.instructions,
        this.officeRuntime,
    );
    private readonly subscriptions = new Subscription();
    private readonly skillSelectionStore = new ChatSkillSelectionStore(
        combineLatest([ this.session.sessionInfo$, this.session.authState$ ]).pipe(
            map(([ session, authenticated ]) => authenticated ? session.user_id ?? null : null),
        ),
    );

    constructor(
        private office: OfficeService,
        private session: OfficeSessionService,
        private chatRepository: OfficeChatRepository,
        private aiModels: AiModelsService,
        private quickActionsService: QuickActionsService,
        private userSettingsApi: UserSettingsApiService,
        private instructions: InstructionsService,
        private readonly officeRuntime = Office,
    )
    {
        super();
        this.subscriptions.add(this.$getSkills().subscribe(skills =>
        {
            this.skillSelectionStore.setSkills(skills);
        }));
        this.subscriptions.add(this.$getCurrentSkill().subscribe(skill =>
        {
            if (skill) { this.setCurrentQuickAction(null); }
        }));
    }

    public override setCurrentSkill(skill: IUserSkill | null): void
    {
        this.skillSelectionStore.setCurrentSkill(skill);
    }

    public override getCurrentSkill(): IUserSkill | null
    {
        return this.skillSelectionStore.getCurrentSkill();
    }

    public override $getCurrentSkill(): Observable<IUserSkill | null>
    {
        return this.skillSelectionStore.$getCurrentSkill();
    }

    public $getSetting(): Observable<IChatSetting>
    {
        return this.officeChatSetting.$getSetting();
    }

    public getSetting(): IChatSetting
    {
        return this.officeChatSetting.getSetting();
    }

    public setSetting(setting: IChatSetting): IChatSetting
    {
        return this.officeChatSetting.setSetting(setting);
    }

    public getSettingOriginal<T>(): T
    {
        return this.officeChatSetting.getSettingOriginal<T>();
    }

    public getQuickActionsDefault(): IQuickActionUser[]
    {
        return this.officeChatSetting.getQuickActionsDefault();
    }

    public $getQuickActions(): Observable<IQuickActionUser[]>
    {
        return this.officeChatSetting.$getQuickActions();
    }

    public setQuickActions(newActions: IQuickActionUser[]): IQuickActionUser[]
    {
        return this.officeChatSetting.setQuickActions(newActions);
    }

    public $getSkills(): Observable<IUserSkill[]>
    {
        return this.officeChatSetting.$getSkills();
    }

    public setSkills(newSkills: IUserSkill[]): IUserSkill[]
    {
        return this.officeChatSetting.setSkills(newSkills);
    }

    public isDefaultSkill(skill: IUserSkill | null): boolean
    {
        return this.officeChatSetting.isDefaultSkill(skill);
    }

    public override setWebSearch(value: boolean): void
    {
        this.officeChatSetting.setWebSearch(value);
    }

    public override getWebSearch(): boolean
    {
        return this.officeChatSetting.getWebSearch();
    }

    public override $getWebSearch(): Observable<boolean>
    {
        return this.officeChatSetting.$getWebSearch();
    }

    public override setImageModel(apiName: string): void
    {
        this.officeChatSetting.setImageModel(apiName);
    }

    public override getImageModel(): string | null
    {
        return this.officeChatSetting.getImageModel();
    }

    public override $getImageModel(): Observable<string | null>
    {
        return this.officeChatSetting.$getImageModel();
    }

    public override setCopilotId(id: string): void
    {
        this.officeChatSetting.setCopilotId(id);
    }

    public override getCopilotId(): string | null
    {
        return this.officeChatSetting.getCopilotId();
    }

    public override $getCopilotId(): Observable<string | null>
    {
        return this.officeChatSetting.$getCopilotId();
    }

    public override onDestroy(): void
    {
        this.subscriptions.unsubscribe();
        this.skillSelectionStore.onDestroy();
        this.officeChatSetting.onDestroy();
    }
}
